import type { ProductPageContent } from '@/content/products';
import { accentBorder, accentRingDot, accentSoftBg, accentText } from '@/lib/product-accents';
import { cn } from '@/lib/utils';

export function ProductSteps({ product }: { product: ProductPageContent }) {
  return (
    <section
      aria-labelledby={`${product.id}-steps-heading`}
      className="px-4 py-6 sm:px-6 sm:py-8 lg:px-8"
    >
      <div className="mx-auto w-full max-w-7xl">
        <p className={cn('mb-3 text-xs font-medium tracking-[0.2em] uppercase', accentText[product.accent])}>
          Method
        </p>
        <h2
          id={`${product.id}-steps-heading`}
          className="font-display text-foreground text-2xl font-semibold tracking-tight sm:text-3xl"
        >
          {product.stepsHeading}
        </h2>
        {product.stepsIntro ? (
          <p className="text-muted-foreground mt-3 max-w-3xl text-base leading-relaxed sm:text-lg">
            {product.stepsIntro}
          </p>
        ) : null}

        <ol className="mt-10 grid gap-4 md:grid-cols-3">
          {product.steps.map((step) => (
            <li
              key={step.number}
              className={cn(
                'border-border/70 bg-card/50 relative rounded-2xl border p-6 backdrop-blur-sm',
                accentBorder[product.accent]
              )}
            >
              <div className="mb-4 flex items-center gap-3">
                <span
                  className={cn('size-2.5 rounded-full', accentRingDot[product.accent])}
                  aria-hidden
                />
                <span
                  className={cn(
                    'font-display inline-flex min-w-12 items-center justify-center rounded-lg px-2 py-1 text-sm font-semibold tabular-nums',
                    accentSoftBg[product.accent],
                    accentText[product.accent]
                  )}
                >
                  {step.number}
                </span>
              </div>
              <h3 className="font-display text-foreground text-xl font-semibold tracking-tight">
                {step.title}
              </h3>
              <p className="text-muted-foreground mt-3 text-sm leading-relaxed sm:text-base">{step.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
